import { zodResolver } from "@hookform/resolvers/zod"
import {
  Loader2,
  MapPinIcon,
  SaveIcon,
  UserIcon,
} from "lucide-react"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { FRONTEND_ROUTES } from "@/constants"
import {
  formatDocument,
  formatPhoneNumber,
} from "@/helpers/format"
import useChangeCustomer from "@/hooks/use-change-customer"
import { useCustomerById } from "@/hooks/use-customer-by-id"
import { toast } from "@/hooks/use-toast"
import type { EditCustomerSchemaProps } from "@/schemas/edit-customer"
import { editCustomerSchema } from "@/schemas/edit-customer"

import {
  InfoItem,
  SectionCard,
} from "../../shared/detail-components"
import { CustomerDetailsSkeleton } from "../details/customer-details-skeleton"

export const CustomerEditForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { customerId } = useParams<{ customerId: string }>()
  const { data, isFetching } = useCustomerById(customerId!)
  const { mutateAsync, isPending } = useChangeCustomer(
    customerId!
  )

  const phoneNumber =
    data?.phone?.areaCode && data?.phone?.number
      ? (formatPhoneNumber(
          `${data.phone.areaCode}${data.phone.number}`
        ) ?? "")
      : ""

  const maskPhone = (value: string) => {
    const digits = value.replaceAll(/\D/g, "").slice(0, 11)
    if (digits.length <= 2) return digits
    if (digits.length <= 7)
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
    return `(${digits.slice(0, 2)}) ${digits.slice(2, digits.length - 4)}-${digits.slice(digits.length - 4)}`
  }

  const schema = useMemo(() => editCustomerSchema(t), [t])

  const form = useForm<EditCustomerSchemaProps>({
    resolver: zodResolver(schema),
    values: {
      email: data?.email ?? "",
      mobilePhone: phoneNumber,
    },
  })

  const handleSubmit = form.handleSubmit(
    async (formData) => {
      try {
        await mutateAsync({
          customerId: customerId!,
          email: formData.email,
          mobilePhone: formData.mobilePhone.replaceAll(
            /\D/g,
            ""
          ),
        })
        toast({
          title: t("pages.customers.edit.success"),
          variant: "success",
        })
        navigate(
          `${FRONTEND_ROUTES.CUSTOMERS.href}/details/${customerId}`
        )
      } catch {
        toast({
          title: t("pages.customers.edit.error"),
          variant: "destructive",
        })
      }
    }
  )

  if (isFetching) {
    return <CustomerDetailsSkeleton />
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-destructive">
            {t("pages.customers.details.loadError")}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-muted-foreground">
            {t("pages.customers.edit.title")}
          </h2>
          <h1 className="page-title">
            {data.firstName} {data.lastName}
          </h1>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <section className="grid gap-4 lg:grid-cols-2">
          <SectionCard
            title={t(
              "pages.customers.details.personalInfo"
            )}
            icon={<UserIcon size={16} />}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoItem
                label="ID"
                value={data.id}
                copyable
              />
              <InfoItem
                label={t("pages.customers.table.name")}
                value={`${data.firstName} ${data.lastName}`}
              />
              <InfoItem
                label={t("pages.customers.table.document")}
                value={
                  data.document
                    ? formatDocument(data.document)
                    : undefined
                }
                copyable
              />
              <InfoItem
                label={t("common.documentType")}
                value={data.documentType}
              />
            </div>
          </SectionCard>

          <SectionCard
            title={t("pages.customers.details.addressInfo")}
            icon={<MapPinIcon size={16} />}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoItem
                label={t("pages.customers.details.street")}
                value={
                  data.address
                    ? `${data.address.street}, ${data.address.streetNumber}`
                    : undefined
                }
              />
              <InfoItem
                label={t(
                  "pages.customers.details.neighborhood"
                )}
                value={data.address?.neighborhood}
              />
              <InfoItem
                label={t("pages.customers.details.city")}
                value={
                  data.address
                    ? `${data.address.city} - ${data.address.state}`
                    : undefined
                }
              />
              <InfoItem
                label={t("common.zipCode")}
                value={data.address?.zipCode}
              />
            </div>
          </SectionCard>
        </section>

        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <SectionCard
              title={t(
                "pages.customers.edit.editableFields"
              )}
              icon={<UserIcon size={16} />}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("pages.customers.table.email")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t(
                            "pages.customers.table.email"
                          )}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mobilePhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("pages.customers.table.phone")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="(11) 99999-9999"
                          maxLength={15}
                          {...field}
                          onChange={(e) => {
                            const masked = maskPhone(
                              e.target.value
                            )
                            field.onChange(masked)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </SectionCard>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  navigate(
                    `${FRONTEND_ROUTES.CUSTOMERS.href}/details/${customerId}`
                  )
                }
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <Loader2
                    size={16}
                    className="mr-2 animate-spin"
                  />
                ) : (
                  <SaveIcon size={16} className="mr-2" />
                )}
                {t("common.save")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
